import { useContext, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { DataContext, JsonContext, UserContext } from '../../../shared/context'

import Sidebar from '../../../components/Sidebar/Sidebar'
import Header from '../../../components/Header/Header'

import AppRouter from '../Router'

import AllLinksArray from './AllLinks'
import useTenant from './useTenant'
import NoTenantSelected from './NoTenantSelected'
import logRedirect, { logLoginComplete } from '../../../helper/RedirectLogger'
import {
	getFeatureFromPath,
	getTenantIdFromPath,
	setSentryFeatureContext,
	setSentryUserContext,
	trackRouteTransition,
	getNavigationSnapshot,
} from '../../../helper/SentryMonitoring'
import { hasMultipleTenants } from '../../../helper/UserAccess'

export const firstTenant = auth => auth.tenant_id[0] || [tenantAddUser]

const tenantAddUser = Number(process.env.REACT_APP_ADD_USER_TENANT)

const AppCrashFallback = () => (
	<div className="flex flex-col items-start justify-center w-full h-full gap-4 px-6 py-10 rounded-2xl bg-white">
		<div className="text-2xl font-semibold text-accent">
			Ein Fehler ist aufgetreten.
		</div>
		<div className="max-w-2xl text-base text-accent">
			Der aktuelle Bereich konnte nicht geladen werden. Bitte laden Sie die Seite
			neu oder gehen Sie einen Schritt zurück.
		</div>
		<div className="flex gap-3">
			<button
				type="button"
				onClick={() => window.location.reload()}
				className="px-4 py-2 text-white rounded-md bg-primary"
			>
				Neu laden
			</button>
			<button
				type="button"
				onClick={() => window.history.back()}
				className="px-4 py-2 border rounded-md border-accent text-accent"
			>
				Zurück
			</button>
		</div>
	</div>
)

const Pages = () => {
	const { profile, auth, access } = useContext(UserContext)
	const { tenant, changeTenant } = useTenant()
	const location = useLocation()
	const previousPathRef = useRef(location.pathname)
	const moreThanOneTenant = hasMultipleTenants(auth.tenant_id)

	const isTenantAddUser = (tenant?.id || tenantAddUser) === tenantAddUser
	const usecaseAttributes = { access, isTenantAddUser }
	const activeTenantId =
		tenant?.id || getTenantIdFromPath(location.pathname) || null
	const currentFeature = getFeatureFromPath(location.pathname)
	const isSearchRoute = currentFeature === 'search'

	useEffect(() => {
		if (auth && profile) {
			logRedirect('Pages', {
				action: 'User reached dashboard',
				username: profile?.username,
				userId: profile?.id,
				tenantIds: auth?.tenant_id || [],
			})

			// Sende Login-Complete Event an Sentry mit allen gesammelten Breadcrumbs
			logLoginComplete(profile, auth)
		}
	}, [auth, profile])

	useEffect(() => {
		const previousPath = previousPathRef.current

		if (previousPath !== location.pathname) {
			trackRouteTransition({
				fromPath: previousPath,
				toPath: location.pathname,
				trigger: 'pages_location_effect',
				extra: {
					currentFeature,
					activeTenantId,
					userId: profile?.id,
					username: profile?.username,
				},
			})
		}

		previousPathRef.current = location.pathname
	}, [
		activeTenantId,
		currentFeature,
		location.pathname,
		profile?.id,
		profile?.username,
	])

	useEffect(() => {
		if (!auth || !profile) {
			return
		}

		setSentryUserContext(profile, auth, location.pathname)
		setSentryFeatureContext({
			feature: currentFeature,
			tenantId: activeTenantId,
			path: location.pathname,
			extra: {
				tenantIds: auth?.tenant_id || [],
				currentPath: location.pathname,
			},
		})
	}, [activeTenantId, auth, currentFeature, location.pathname, profile])

	return (
		<JsonContext.Provider
			value={{
				AllLinks: AllLinksArray(moreThanOneTenant, usecaseAttributes),
				moreThanOneTenant,
			}}
		>
			<DataContext.Provider value={{ tenant, changeTenant }}>
				{tenant && <Sidebar />}
				<div className="flex flex-col overflow-auto grow print:overflow-visible">
					{tenant && <Header tenant={tenant} />}
					<div className="flex flex-col w-full p-5 grow">
						<Sentry.ErrorBoundary
							key={location.pathname}
							fallback={<AppCrashFallback />}
							beforeCapture={scope => {
								scope.setTag('feature', currentFeature)
								scope.setTag('isSearchRoute', isSearchRoute)

								if (activeTenantId) {
									scope.setTag('activeTenantId', activeTenantId)
								}

								if (profile?.id) {
									scope.setTag('userId', profile.id)
								}

								scope.setContext('route', {
									pathname: location.pathname,
									tenantId: activeTenantId,
									feature: currentFeature,
									isSearchRoute,
									lastNavigation: getNavigationSnapshot(),
								})
							}}
						>
							<Routes>
								<Route path="/" element={<NoTenantSelected />} />
								<Route path="/tenant/:tenantId/*" element={<AppRouter />} />
								<Route path="*" element={<Navigate to="/" replace />} />
							</Routes>
						</Sentry.ErrorBoundary>
					</div>
				</div>
			</DataContext.Provider>
		</JsonContext.Provider>
	)
}

export default Pages
